import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Clock, MapPin, Users, Calendar, Check, ChevronLeft, 
  Star, Camera, Sun, Shield, Package, X, 
  ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon
} from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const TourDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    number_of_people: 1,
    preferred_date: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchTourDetails();
    window.scrollTo(0, 0);
  }, [id]);

  const fetchTourDetails = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/tours/${id}`);
      setTour(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching tour details:', error);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API_BASE_URL}/api/inquiry/tour`, {
        ...formData,
        tour_name: tour.name
      });
      setSubmitted(true);
      setShowInquiryForm(false);
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      alert('There was an error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Safely parse JSON fields
  const itinerary = tour?.itinerary ? (typeof tour.itinerary === 'string' ? JSON.parse(tour.itinerary) : tour.itinerary) : [];
  const gallery = tour?.gallery ? (typeof tour.gallery === 'string' ? JSON.parse(tour.gallery) : tour.gallery) : [];
  const whatToBring = tour?.what_to_bring ? (typeof tour.what_to_bring === 'string' ? JSON.parse(tour.what_to_bring) : tour.what_to_bring) : [];
  const includedItems = tour?.included ? (typeof tour.included === 'string' ? JSON.parse(tour.included) : tour.included) : [];

  // Helper function to display price safely
  const getDisplayPrice = (price) => {
    if (typeof price === 'number') return price.toLocaleString();
    if (typeof price === 'string') {
      const num = parseFloat(price.replace(/,/g, ''));
      if (!isNaN(num)) return num.toLocaleString();
      return price;
    }
    return '0';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading tour details...</p>
        </div>
      </div>
    );
  }

  if (!tour) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <div className="bg-white rounded-lg p-12 shadow-sm">
          <h2 className="text-2xl font-bold text-gray-600 mb-4">Tour not found</h2>
          <button 
            onClick={() => navigate('/tours')} 
            className="bg-yellow-500 text-gray-900 px-6 py-2 rounded-lg hover:bg-yellow-600 transition"
          >
            Back to Tours
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Section */}
      <div className="relative h-[50vh] md:h-[60vh] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent z-10"></div>
        <img 
          src={gallery[0] || tour.image_url} 
          alt={tour.name} 
          className="w-full h-full object-cover"
        />
        <div className="absolute bottom-0 left-0 right-0 z-20 p-6 md:p-12">
          <div className="container mx-auto">
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="bg-yellow-500 text-gray-900 px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                <Clock size={14} className="mr-1" />
                {tour.duration}
              </span>
              {tour.featured && (
                <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                  <Star size={14} className="mr-1 fill-current" />
                  Featured
                </span>
              )}
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2">{tour.name}</h1>
            <p className="text-gray-200 text-lg max-w-2xl">{tour.description}</p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tab Navigation */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="flex border-b overflow-x-auto">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`px-4 py-3 text-sm font-medium transition whitespace-nowrap ${
                    activeTab === 'overview'
                      ? 'text-yellow-600 border-b-2 border-yellow-500'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab('itinerary')}
                  className={`px-4 py-3 text-sm font-medium transition whitespace-nowrap ${
                    activeTab === 'itinerary'
                      ? 'text-yellow-600 border-b-2 border-yellow-500'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Itinerary
                </button>
                <button
                  onClick={() => setActiveTab('included')}
                  className={`px-4 py-3 text-sm font-medium transition whitespace-nowrap ${
                    activeTab === 'included'
                      ? 'text-yellow-600 border-b-2 border-yellow-500'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Included
                </button>
                <button
                  onClick={() => setActiveTab('info')}
                  className={`px-4 py-3 text-sm font-medium transition whitespace-nowrap ${
                    activeTab === 'info'
                      ? 'text-yellow-600 border-b-2 border-yellow-500'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Practical Info
                </button>
              </div>

              <div className="p-6">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div>
                    <p className="text-gray-600 leading-relaxed mb-6">{tour.description}</p>
                    
                    {gallery.length > 0 && (
                      <div>
                        <h3 className="text-lg font-bold text-gray-800 mb-3">Tour Gallery</h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {gallery.slice(0, 6).map((img, idx) => (
                            <div
                              key={idx}
                              className="relative rounded-lg overflow-hidden cursor-pointer aspect-square"
                              onClick={() => {
                                setSelectedImageIndex(idx);
                                setShowLightbox(true);
                              }}
                            >
                              <img src={img} alt="" className="w-full h-full object-cover hover:scale-105 transition" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Itinerary Tab */}
                {activeTab === 'itinerary' && itinerary.length > 0 && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-4">Daily Itinerary</h3>
                    <div className="space-y-4">
                      {itinerary.map((day, idx) => (
                        <div key={idx} className="flex gap-4">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center text-gray-900 font-bold text-sm">
                              {idx + 1}
                            </div>
                          </div>
                          <div className="flex-1 pb-3 border-b border-gray-100">
                            <p className="text-gray-700">{day}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Included Tab */}
                {activeTab === 'included' && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-3">What's Included</h3>
                    <div className="grid md:grid-cols-2 gap-2 mb-6">
                      {includedItems.map((item, i) => (
                        <div key={i} className="flex items-center space-x-2">
                          <Check size={16} className="text-green-500" />
                          <span className="text-gray-600">{item}</span>
                        </div>
                      ))}
                    </div>

                    {whatToBring.length > 0 && (
                      <>
                        <h3 className="text-lg font-bold text-gray-800 mb-3">What to Bring</h3>
                        <div className="grid md:grid-cols-2 gap-2">
                          {whatToBring.map((item, i) => (
                            <div key={i} className="flex items-center space-x-2">
                              <Package size={16} className="text-blue-500" />
                              <span className="text-gray-600">{item}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Practical Info Tab */}
                {activeTab === 'info' && (
                  <div className="space-y-4">
                    {tour.meeting_point && (
                      <div className="flex items-start space-x-3">
                        <MapPin size={18} className="text-yellow-500 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-gray-800">Meeting Point</h4>
                          <p className="text-gray-600 text-sm">{tour.meeting_point}</p>
                        </div>
                      </div>
                    )}
                    
                    {tour.best_time && (
                      <div className="flex items-start space-x-3">
                        <Sun size={18} className="text-yellow-500 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-gray-800">Best Time to Visit</h4>
                          <p className="text-gray-600 text-sm">{tour.best_time}</p>
                        </div>
                      </div>
                    )}

                    {tour.cancelation_policy && (
                      <div className="bg-red-50 rounded-lg p-4">
                        <div className="flex items-start space-x-3">
                          <Shield size={18} className="text-red-500 mt-0.5" />
                          <div>
                            <h4 className="font-semibold text-red-700">Cancellation Policy</h4>
                            <p className="text-red-600 text-sm">{tour.cancelation_policy}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Booking Card */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="bg-yellow-500 p-4 text-center">
                  <div className="text-3xl font-bold text-gray-900">{getDisplayPrice(tour.price)}</div>
                  <p className="text-gray-800 text-sm">per person</p>
                </div>

                <div className="p-4 border-b">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-gray-600">Duration</span>
                    <span className="font-semibold">{tour.duration}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-gray-600">Group Size</span>
                    <span className="font-semibold">2-15 people</span>
                  </div>
                </div>

                {!submitted ? (
                  !showInquiryForm ? (
                    <div className="p-4">
                      <button
                        onClick={() => setShowInquiryForm(true)}
                        className="w-full bg-yellow-500 text-gray-900 py-3 rounded-lg hover:bg-yellow-600 transition font-semibold"
                      >
                        Book This Tour
                      </button>
                    </div>
                  ) : (
                    <div className="p-4">
                      <h3 className="font-bold text-gray-800 mb-3">Booking Request</h3>
                      <form onSubmit={handleSubmit} className="space-y-3">
                        <input
                          type="text"
                          name="name"
                          placeholder="Full Name *"
                          required
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full p-2 border rounded-lg"
                        />
                        <input
                          type="email"
                          name="email"
                          placeholder="Email *"
                          required
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full p-2 border rounded-lg"
                        />
                        <input
                          type="tel"
                          name="phone"
                          placeholder="Phone *"
                          required
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full p-2 border rounded-lg"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="number"
                            name="number_of_people"
                            placeholder="People"
                            required
                            min="1"
                            value={formData.number_of_people}
                            onChange={handleInputChange}
                            className="w-full p-2 border rounded-lg"
                          />
                          <input
                            type="date"
                            name="preferred_date"
                            required
                            value={formData.preferred_date}
                            onChange={handleInputChange}
                            className="w-full p-2 border rounded-lg"
                          />
                        </div>
                        <textarea
                          name="message"
                          rows="2"
                          placeholder="Special requests"
                          value={formData.message}
                          onChange={handleInputChange}
                          className="w-full p-2 border rounded-lg"
                        ></textarea>
                        <div className="flex space-x-2">
                          <button type="submit" disabled={submitting} className="flex-1 bg-yellow-500 py-2 rounded-lg hover:bg-yellow-600 transition">
                            {submitting ? 'Sending...' : 'Send'}
                          </button>
                          <button type="button" onClick={() => setShowInquiryForm(false)} className="flex-1 border py-2 rounded-lg hover:bg-gray-50">
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  )
                ) : (
                  <div className="p-6 text-center">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Check size={24} className="text-green-500" />
                    </div>
                    <h3 className="font-bold text-gray-800 mb-1">Request Sent!</h3>
                    <p className="text-gray-600 text-sm">We'll confirm within 24 hours.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {showLightbox && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center" onClick={() => setShowLightbox(false)}>
          <div className="relative max-w-5xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowLightbox(false)} className="absolute -top-12 right-0 text-white hover:text-yellow-500">
              <X size={32} />
            </button>
            <img src={gallery[selectedImageIndex]} alt="" className="w-full h-auto max-h-[80vh] object-contain rounded-lg" />
            <div className="flex justify-between items-center mt-4">
              <button onClick={() => setSelectedImageIndex(prev => (prev - 1 + gallery.length) % gallery.length)} className="bg-white/20 p-2 rounded-full hover:bg-white/30">
                <ChevronLeftIcon size={24} />
              </button>
              <span className="text-white text-sm">{selectedImageIndex + 1} / {gallery.length}</span>
              <button onClick={() => setSelectedImageIndex(prev => (prev + 1) % gallery.length)} className="bg-white/20 p-2 rounded-full hover:bg-white/30">
                <ChevronRightIcon size={24} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TourDetail;